/**
 * A long-lived session around `sam.worker.js` — load once, encode once per
 * slice, decode on every prompt change. `lib/worker-client.ts`'s `runWorker`
 * doesn't fit here: it tears down its listeners after the first non-progress
 * reply, which is right for a one-shot job (skull-strip a volume) and wrong
 * for a worker this code calls repeatedly over one open panel.
 */
import { appPath } from "../../lib/assets";


export interface SamDecodeResult {
  mask: Float32Array; // 1024x1024 logits, >0 is foreground
  iou: number | null;
}

export interface SamSession {
  load(onProgress: (stage: string) => void): Promise<void>;
  encode(pixels: Float32Array): Promise<void>;
  decode(points: number[], labels: number[]): Promise<SamDecodeResult>;
  dispose(): void;
}

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  onProgress?: (stage: string) => void;
}

export function createSamSession(): SamSession {
  const worker = new Worker(appPath("workers/sam.worker.js"), { type: "module" });
  let seq = 0;
  const pending = new Map<number, Pending>();

  worker.addEventListener("message", (e: MessageEvent) => {
    const { id, type } = e.data as { id: number; type: string };
    const entry = pending.get(id);
    if (!entry) return;
    if (type === "progress") {
      entry.onProgress?.(e.data.stage as string);
      return;
    }
    pending.delete(id);
    if (type === "error") entry.reject(new Error(e.data.message as string));
    else entry.resolve(e.data);
  });
  worker.addEventListener("error", (e: ErrorEvent) => {
    for (const [id, entry] of pending) {
      entry.reject(new Error(e.message || "sam worker failed"));
      pending.delete(id);
    }
  });

  function call<T>(
    type: string,
    payload: Record<string, unknown>,
    transfer: Transferable[] = [],
    onProgress?: (stage: string) => void,
  ): Promise<T> {
    const id = ++seq;
    return new Promise((resolve, reject) => {
      pending.set(id, { resolve: resolve as (v: unknown) => void, reject, onProgress });
      worker.postMessage({ id, type, ...payload }, transfer);
    });
  }

  return {
    load: (onProgress) => call("load", {}, [], onProgress).then(() => undefined),
    encode: (pixels) => {
      const buf = pixels.buffer.slice(pixels.byteOffset, pixels.byteOffset + pixels.byteLength);
      return call("encode", { pixels: buf }, [buf]).then(() => undefined);
    },
    decode: (points, labels) =>
      call<{ mask: ArrayBuffer; iou: number | null }>("decode", { points, labels }).then((r) => ({
        mask: new Float32Array(r.mask),
        iou: r.iou,
      })),
    dispose: () => worker.terminate(),
  };
}
